import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { 
  signInWithEmailAndPassword, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence, 
  sendPasswordResetEmail 
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import "./SignupPage.css";

const SignInPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Handle login
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!email || !password) {
      setError("Please fill all fields");
      setLoading(false);
      return;
    }

    try {
      // Set persistence based on "Remember Me"
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update last login timestamp in Firestore
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          await updateDoc(doc(db, "users", user.uid), {
            lastLoginAt: new Date()
          });
        }
      } catch (firestoreError) {
        console.log("Could not update last login:", firestoreError);
        // Continue even if this fails
      }
      
      alert("Logged in successfully!");
      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "Login failed";
      
      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = "No account found with this email";
          break;
        case "auth/wrong-password":
          errorMessage = "Incorrect password";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many failed attempts. Try again later or reset password";
          break;
        default:
          errorMessage = error.message;
      }
      
      setError(errorMessage);
    }

    setLoading(false);
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address to reset password");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      alert("Password reset email sent! Check your inbox and spam folder.");
    } catch (error) {
      let errorMessage = "Failed to send reset email";
      
      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Invalid email address";
      }
      
      setError(errorMessage);
    }
  };

  return (
    <div className="signup-container">
      <form className="signup-form" onSubmit={handleSignIn}>
        <h2>Sign In</h2>

        {error && (
          <div style={{
            padding: "10px",
            backgroundColor: "#ffebee",
            color: "#c62828",
            borderRadius: "4px",
            marginBottom: "15px",
            fontSize: "14px"
          }}>
            {error}
          </div>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />

        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between", 
          marginBottom: "15px",
          width: "100%"
        }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ marginRight: "8px" }}
              disabled={loading}
            />
            <label htmlFor="rememberMe" style={{ fontSize: "14px", color: "#555" }}>
              Remember Me
            </label>
          </div>
          
          <span 
            onClick={handleForgotPassword} 
            style={{ 
              color: "#2196f3", 
              cursor: "pointer", 
              fontSize: "14px",
              fontWeight: 500
            }}
          >
            Forgot Password?
          </span>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={{
            backgroundColor: loading ? "#ccc" : "#2196f3",
            cursor: loading ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Logging in..." : "Sign In"}
        </button>

        <p style={{ marginTop: "20px", fontSize: "14px", textAlign: "center" }}>
          Don't have an account? <Link to="/signup" style={{ color: "#2196f3", fontWeight: 500 }}>Create Account</Link>
        </p>
      </form>
    </div>
  );
};

export default SignInPage;